import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { createProject, updateProject, fetchProjectById, fetchStatuses, fetchCertifiers } from '../services/projectService';
import PolygonMap from './PolygonMap';
import { kml } from '@mapbox/togeojson';

// Lista de departamentos de Colombia
const DEPARTAMENTOS_COLOMBIA = [
  'Amazonas', 'Antioquia', 'Arauca', 'Atlántico', 'Bolívar', 'Boyacá', 'Caldas',
  'Caquetá', 'Casanare', 'Cauca', 'Cesar', 'Chocó', 'Córdoba', 'Cundinamarca',
  'Guainía', 'Guaviare', 'Huila', 'La Guajira', 'Magdalena', 'Meta', 'Nariño',
  'Norte de Santander', 'Putumayo', 'Quindío', 'Risaralda', 'San Andrés y Providencia',
  'Santander', 'Sucre', 'Tolima', 'Valle del Cauca', 'Vaupés', 'Vichada'
];

const ProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    departamento: '',
    hectares: '',
    description: '',
    status: null,
    validatorString: '',
    verifiersString: '',
    certifier: null,
    startDate: null,
    endDate: null,
    pddStartDate: null,
    pddEndDate: null,
    registrationDate: null,
    queryDate: null,
    projectType: '',
    duration: '',
    communitiesInvolved: '',
    developerOrganizations: '',
    collaboratingOrganizations: '',
    link: '',
    locationGeometryWkt: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [certifiers, setCertifiers] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [showPolygonModal, setShowPolygonModal] = useState(false);

  const geoJsonToWkt = (geoJson) => {
    let geometry = geoJson;

    if (geoJson.type === 'FeatureCollection') {
      const feature = geoJson.features.find(f => f.geometry && 
        (f.geometry.type === 'Polygon' || 
         f.geometry.type === 'MultiPolygon' ||
         f.geometry.type === 'Point' ||
         f.geometry.type === 'LineString'));
      if (!feature) {
        throw new Error('No se encontró una geometría válida en la colección (Polygon, MultiPolygon, Point o LineString)');
      }
      geometry = feature.geometry;
    } else if (geoJson.type === 'Feature') {
      geometry = geoJson.geometry;
    }

    if (!geometry) {
      throw new Error('Geometría no encontrada');
    }

    // Handle different geometry types
    if (geometry.type === 'Point') {
      // POINT(lng lat)
      const coordinates = geometry.coordinates;
      return `POINT(${coordinates[0]} ${coordinates[1]})`;
    } else if (geometry.type === 'LineString') {
      // LINESTRING(lng1 lat1, lng2 lat2, ...)
      const coordinates = geometry.coordinates;
      const wktCoords = coordinates.map(coord => `${coord[0]} ${coord[1]}`).join(', ');
      return `LINESTRING(${wktCoords})`;
    } else if (geometry.type === 'Polygon') {
      const coordinates = geometry.coordinates[0]; // Outer ring
      const wktCoords = coordinates.map(coord => `${coord[0]} ${coord[1]}`).join(', ');
      return `POLYGON((${wktCoords}))`;
    } else if (geometry.type === 'MultiPolygon') {
      const polygons = geometry.coordinates.map(polygon => {
        const ring = polygon[0]; // Assume no holes, take first ring
        const wktCoords = ring.map(coord => `${coord[0]} ${coord[1]}`).join(', ');
        return `((${wktCoords}))`;
      });
      return `MULTIPOLYGON(${polygons.join(', ')})`;
    } else {
      throw new Error(`Tipo de geometría no soportado: ${geometry.type}. Solo se soportan Point, LineString, Polygon y MultiPolygon`);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      let geoJson;

      try {
        // Try to parse as GeoJSON
        geoJson = JSON.parse(text);
      } catch (jsonError) {
        // If JSON parsing fails, try to parse as KML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'text/xml');
        geoJson = kml(xmlDoc);
      }

      const wkt = geoJsonToWkt(geoJson);
      setFormData(prev => ({ ...prev, locationGeometryWkt: wkt }));
    } catch (error) {
      console.error('Error processing file:', error);
      // Could add error state here
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Helper function to format date to ISO string (YYYY-MM-DD)
      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().split('T')[0];
      };

      // Convert hectares to number if provided and prepare project data
      const selectedStatus = statuses.find(s => s.id === formData.status);
      const selectedCertifier = certifiers.find(c => c.id === formData.certifier);

      const projectData = {
        ...formData,
        hectares: formData.hectares ? parseFloat(formData.hectares) : null,
        // Convert dates to ISO strings
        startDate: formatDate(formData.startDate),
        endDate: formatDate(formData.endDate),
        pddStartDate: formatDate(formData.pddStartDate),
        pddEndDate: formatDate(formData.pddEndDate),
        registrationDate: formatDate(formData.registrationDate),
        queryDate: formatDate(formData.queryDate),
        // Include full relationship objects
        status: selectedStatus || null,
        certifier: selectedCertifier || null
      };

      if (isEdit) {
        await updateProject(id, projectData);
      } else {
        await createProject(projectData);
      }
      setSuccess(true);

      // Redirect to projects list after a short delay
      setTimeout(() => {
        navigate('/admin/projects/list');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/projects/list');
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setOptionsLoading(true);
        const [statusesData, certifiersData] = await Promise.all([
          fetchStatuses(),
          fetchCertifiers()
        ]);
        setStatuses(statusesData);
        setCertifiers(certifiersData);
      } catch (err) {
        console.error('Error loading options:', err);
        setError('Error al cargar las opciones del formulario');
      } finally {
        setOptionsLoading(false);
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    if (isEdit) {
      const loadProject = async () => {
        try {
          setInitialLoading(true);
          const project = await fetchProjectById(id);
          // Format dates for Calendar component
          const formatDateForCalendar = (dateString) => {
            return dateString ? new Date(dateString) : null;
          };

          setFormData({
            name: project.name || '',
            location: project.location || '',
            departamento: project.departamento || '',
            hectares: project.hectares ? project.hectares.toString() : '',
            description: project.description || '',
            status: project.status ? project.status.id : null,
            validatorString: project.validatorString || '',
            verifiersString: project.verifiersString || '',
            certifier: project.certifier ? project.certifier.id : null,
            startDate: formatDateForCalendar(project.startDate),
            endDate: formatDateForCalendar(project.endDate),
            pddStartDate: formatDateForCalendar(project.pddStartDate),
            pddEndDate: formatDateForCalendar(project.pddEndDate),
            registrationDate: formatDateForCalendar(project.registrationDate),
            queryDate: formatDateForCalendar(project.queryDate),
            projectType: project.projectType || '',
            duration: project.duration || '',
            communitiesInvolved: project.communitiesInvolved || '',
            developerOrganizations: project.developerOrganizations || '',
            collaboratingOrganizations: project.collaboratingOrganizations || '',
            link: project.link || '',
            locationGeometryWkt: project.locationGeometryWkt || ''
          });
        } catch (err) {
          console.error('Error loading project:', err);
          setError('Error al cargar el proyecto');
        } finally {
          setInitialLoading(false);
        }
      };

      loadProject();
    }
  }, [id, isEdit]);

  if (initialLoading) {
    return (
      <div className="p-4">
        <Card className="shadow-2">
          <div className="text-center">
            <ProgressSpinner />
            <p>Cargando proyecto...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="p-4">
        <Card className="shadow-2">
          <div className="text-center">
            <Message
              severity="success"
              text={`Proyecto ${isEdit ? 'actualizado' : 'creado'} exitosamente. Redirigiendo...`}
              className="w-full mb-4"
            />
            <ProgressSpinner />
          </div>
        </Card>
      </div>
    );
  }

  // Render field helper
  const renderField = (id, label, component, colClass = "col-12 md:col-6", required = false) => (
    <div className={`${colClass} mb-4`}>
      <label htmlFor={id} className="block text-900 font-medium mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {component}
    </div>
  );

  const renderSectionHeader = (icon, title, description) => (
    <div className="col-12 mb-3 mt-2">
      <div className="flex align-items-center gap-2">
        <i className={`pi ${icon} text-primary text-xl`}></i>
        <h3 className="m-0 text-900">{title}</h3>
      </div>
      {description && <p className="text-600 text-sm m-0 mt-1">{description}</p>}
      <Divider className="my-3" />
    </div>
  );

  return (
    <div className="p-4">
      <Card title={isEdit ? "Editar Proyecto" : "Nuevo Proyecto"} className="shadow-2">
        {error && (
          <Message
            severity="error"
            text={`Error al crear el proyecto: ${error}`}
            className="w-full mb-4"
          />
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid formgrid">
            {/* ==================== SECCIÓN 1: INFORMACIÓN BÁSICA ==================== */}
            {renderSectionHeader('pi-info-circle', '1. Información Básica del Proyecto', 'Datos principales del proyecto')}

            {renderField(
              'name',
              'Nombre del Proyecto',
              <InputText
                id="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Ingrese el nombre del proyecto"
                required
              />,
              "col-12",
              true
            )}

            {renderField(
              'projectType',
              'Tipo de Proyecto',
              <InputText
                id="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Ej: REDD+, Bosques, Agricultura Sostenible"
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'description',
              'Descripción',
              <InputTextarea
                id="description"
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Describa el proyecto detalladamente"
              />,
              "col-12"
            )}

            {/* ==================== SECCIÓN 2: UBICACIÓN ==================== */}
            {renderSectionHeader('pi-map-marker', '2. Ubicación Geográfica', 'Datos de ubicación y extensión del proyecto')}

            {renderField(
              'departamento',
              'Departamento',
              <Dropdown
                id="departamento"
                value={formData.departamento}
                onChange={(e) => setFormData(prev => ({ ...prev, departamento: e.value }))}
                options={DEPARTAMENTOS_COLOMBIA}
                placeholder="Seleccione un departamento"
                className="w-full"
                showClear
                filter
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'location',
              'Municipios / Ubicación',
              <div className="flex gap-2">
                <InputText
                  id="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="flex-1"
                  placeholder="Ej: Bogotá, Medellín, Cali"
                />
                <Button
                  type="button"
                  icon="pi pi-map"
                  className="p-button-secondary"
                  tooltip="Cargar polígono"
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => setShowPolygonModal(true)}
                />
              </div>,
              "col-12 md:col-6"
            )}

            {renderField(
              'hectareas',
              'Área (Hectáreas)',
              <InputText
                id="hectares"
                type="number"
                value={formData.hectares}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Ingrese el área en hectáreas"
                min="0"
                step="0.01"
              />,
              "col-12 md:col-6"
            )}

            {formData.locationGeometryWkt && (
              <div className="col-12 md:col-6 mb-4">
                <label className="block text-900 font-medium mb-2">
                  Polígono Cargado
                </label>
                <div className="flex align-items-center gap-2 p-3 border-1 border-round surface-100 border-radius">
                  <i className="pi pi-check-circle text-green-500"></i>
                  <span className="text-700">Geometría cargada correctamente</span>
                  <Button
                    type="button"
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm ml-auto"
                    tooltip="Ver polígono"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => setShowPolygonModal(true)}
                  />
                </div>
              </div>
            )}

            {/* ==================== SECCIÓN 3: ESTADO Y CERTIFICACIÓN ==================== */}
            {renderSectionHeader('pi-verified', '3. Estado y Certificación', 'Estado actual del proyecto y certificadora')}

            {renderField(
              'status',
              'Estado del Proyecto',
              <Dropdown
                id="status"
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.value }))}
                options={statuses}
                optionLabel="name"
                optionValue="id"
                placeholder={optionsLoading ? "Cargando..." : "Seleccione un estado"}
                className="w-full"
                disabled={optionsLoading}
                showClear
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'certifier',
              'Certificadora',
              <Dropdown
                id="certifier"
                value={formData.certifier}
                onChange={(e) => setFormData(prev => ({ ...prev, certifier: e.value }))}
                options={certifiers}
                optionLabel="name"
                optionValue="id"
                placeholder={optionsLoading ? "Cargando..." : "Seleccione una certificadora"}
                className="w-full"
                disabled={optionsLoading}
                showClear
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'link',
              'Enlace / URL del Proyecto',
              <InputText
                id="link"
                value={formData.link}
                onChange={handleInputChange}
                className="w-full"
                placeholder="https://..."
                type="url"
              />,
              "col-12"
            )}

            {/* ==================== SECCIÓN 4: FECHAS DEL PROYECTO ==================== */}
            {renderSectionHeader('pi-calendar', '4. Fechas del Proyecto', 'Cronograma y fechas importantes')}

            {renderField(
              'startDate',
              'Fecha de Inicio',
              <Calendar
                id="startDate"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.value }))}
                dateFormat="dd/mm/yy"
                placeholder="Seleccione fecha de inicio"
                className="w-full"
                showIcon
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'endDate',
              'Fecha de Fin',
              <Calendar
                id="endDate"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.value }))}
                dateFormat="dd/mm/yy"
                placeholder="Seleccione fecha de fin"
                className="w-full"
                showIcon
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'duration',
              'Duración (años)',
              <InputText
                id="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Ej: 30 años"
              />,
              "col-12 md:col-6"
            )}

            {/* ==================== SECCIÓN 5: DOCUMENTO PDD ==================== */}
            {renderSectionHeader('pi-file', '5. Documento PDD', 'Documento de Diseño del Proyecto')}

            {renderField(
              'pddStartDate',
              'PDD - Fecha Inicio',
              <Calendar
                id="pddStartDate"
                value={formData.pddStartDate}
                onChange={(e) => setFormData(prev => ({ ...prev, pddStartDate: e.value }))}
                dateFormat="dd/mm/yy"
                placeholder="Seleccione fecha de inicio PDD"
                className="w-full"
                showIcon
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'pddEndDate',
              'PDD - Fecha Fin',
              <Calendar
                id="pddEndDate"
                value={formData.pddEndDate}
                onChange={(e) => setFormData(prev => ({ ...prev, pddEndDate: e.value }))}
                dateFormat="dd/mm/yy"
                placeholder="Seleccione fecha de fin PDD"
                className="w-full"
                showIcon
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'registrationDate',
              'Fecha de Registro',
              <Calendar
                id="registrationDate"
                value={formData.registrationDate}
                onChange={(e) => setFormData(prev => ({ ...prev, registrationDate: e.value }))}
                dateFormat="dd/mm/yy"
                placeholder="Seleccione fecha de registro"
                className="w-full"
                showIcon
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'queryDate',
              'Fecha de Consulta',
              <Calendar
                id="queryDate"
                value={formData.queryDate}
                onChange={(e) => setFormData(prev => ({ ...prev, queryDate: e.value }))}
                dateFormat="dd/mm/yy"
                placeholder="Seleccione fecha de consulta"
                className="w-full"
                showIcon
              />,
              "col-12 md:col-6"
            )}

            {/* ==================== SECCIÓN 6: ORGANIZACIONES ==================== */}
            {renderSectionHeader('pi-users', '6. Organizaciones y Comunidades', 'Entidades involucradas en el proyecto')}

            {renderField(
              'validatorString',
              'Entidad Validadora',
              <InputText
                id="validatorString"
                value={formData.validatorString}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Nombre de la entidad validadora"
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'verifiersString',
              'Verificadores',
              <InputTextarea
                id="verifiersString"
                rows={2}
                value={formData.verifiersString}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Nombres de verificadores (separados por comas)"
              />,
              "col-12 md:col-6"
            )}

            {renderField(
              'developerOrganizations',
              'Organizaciones Desarrolladoras',
              <InputTextarea
                id="developerOrganizations"
                rows={2}
                value={formData.developerOrganizations}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Organizaciones que desarrollan el proyecto"
              />,
              "col-12"
            )}

            {renderField(
              'collaboratingOrganizations',
              'Organizaciones Colaboradoras',
              <InputTextarea
                id="collaboratingOrganizations"
                rows={2}
                value={formData.collaboratingOrganizations}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Organizaciones que colaboran en el proyecto"
              />,
              "col-12"
            )}

            {renderField(
              'communitiesInvolved',
              'Comunidades Involucradas',
              <InputTextarea
                id="communitiesInvolved"
                rows={2}
                value={formData.communitiesInvolved}
                onChange={handleInputChange}
                className="w-full"
                placeholder="Comunidades locales involucradas en el proyecto"
              />,
              "col-12"
            )}

            {/* ==================== BOTONES DE ACCIÓN ==================== */}
            <div className="col-12 mt-4">
              <Divider />
              <div className="flex justify-content-end gap-2">
                <Button
                  type="button"
                  label="Cancelar"
                  icon="pi pi-times"
                  className="p-button-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  label={loading ? 'Guardando...' : (isEdit ? 'Actualizar Proyecto' : 'Crear Proyecto')}
                  icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  className="p-button-primary"
                  disabled={loading || optionsLoading || !formData.name.trim()}
                />
              </div>
            </div>
          </div>
        </form>
      </Card>

      <Dialog
        header="Cargar Polígono del Proyecto"
        visible={showPolygonModal}
        onHide={() => setShowPolygonModal(false)}
        style={{ width: '80vw', height: '80vh' }}
        maximizable
        className="p-fluid"
        footer={
          <div className="flex justify-content-end">
            <Button
              label="Cerrar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowPolygonModal(false)}
            />
          </div>
        }
      >
        <div className="mb-3">
          <label htmlFor="geojson-upload" className="block text-900 font-medium mb-2">
            Cargar archivo GeoJSON o KML
          </label>
          <input
            type="file"
            id="geojson-upload"
            accept=".geojson,.json,.kml"
            onChange={handleFileUpload}
            className="w-full"
          />
          <small className="text-600">Archivos soportados: .geojson, .json, .kml</small>
        </div>

        {formData.locationGeometryWkt ? (
          <PolygonMap wkt={formData.locationGeometryWkt} />
        ) : (
          <div className="text-center p-4">
            <Message
              severity="info"
              text="Cargue un archivo GeoJSON o KML para visualizar el polígono"
              className="w-full"
            />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ProjectForm;
